import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Languages, Loader2, MailCheck } from "lucide-react";

const BG_IMAGE = "https://images.unsplash.com/photo-1764314138160-5f04f4a50dae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxwaHlzaWNhbCUyMHRoZXJhcGlzdCUyMGNsaW5pY3xlbnwwfHx8fDE3NzgxMDM2NzJ8MA&ixlib=rb-4.1.0&q=85";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", {
        email,
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)), url(${BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-8" data-testid="forgot-password-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center">
            <Languages className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-xl tracking-tight text-zinc-900">Clinico Translate</span>
        </div>

        {!sent ? (
          <>
            <h2 className="font-heading text-3xl tracking-tight text-zinc-900 mb-2">Forgot password?</h2>
            <p className="text-zinc-600 mb-8">
              Enter the email you signed up with. We'll send you a link to set a new password.
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="email" className="text-sm uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-12 text-base"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-12 text-base" data-testid="forgot-submit-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="forgot-success">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-5">
              <MailCheck className="w-6 h-6 text-emerald-700" />
            </div>
            <h2 className="font-heading text-2xl tracking-tight text-zinc-900 mb-2">Check your inbox</h2>
            <p className="text-zinc-600">
              If an account exists for <span className="font-medium text-zinc-900">{email}</span>, you'll receive a reset link within a minute.
              The link is valid for 60 minutes.
            </p>
            <p className="text-sm text-zinc-500 mt-6">
              Didn't get it? Check spam, or{" "}
              <button onClick={() => setSent(false)} className="underline underline-offset-2 text-zinc-900" data-testid="forgot-try-again">try again</button>.
            </p>
          </div>
        )}

        <p className="text-sm text-zinc-600 mt-8 text-center">
          Remembered it?{" "}
          <Link to="/login" className="text-zinc-900 underline underline-offset-4 font-medium" data-testid="forgot-back-to-login">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
